import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Lock, BookOpen, FileText, Download, Eye, ArrowRight, CheckCircle2, Circle, PlayCircle, ClipboardCheck, Award } from "lucide-react";
import { api, errMsg, useSeo, embedUrl } from "../lib/api";
import { useAuth } from "../context/AppContext";
import { Section, Loading, Empty, Badge } from "../components/Common";
import { Progress } from "../components/ui/progress";
import LessonQuiz from "../components/LessonQuiz";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
export const fileSrc = (u) => (u && u.startsWith("/api/") ? API_ORIGIN + u : u);
const rewrite = (html) => DOMPurify.sanitize((html || "").replaceAll('src="/api/files/', `src="${API_ORIGIN}/api/files/`));

export const LessonCover = ({ l, i }) => (
  <div className="aspect-[16/10] relative overflow-hidden bg-zinc-950">
    {l.cover_url ? <img src={fileSrc(l.cover_url)} alt={l.title} loading="lazy" className="w-full h-full object-cover" /> : (
      <div className="absolute inset-0 grid-lines p-5 flex flex-col justify-between text-white">
        <div className="flex justify-between items-start"><img src="/ada-logo.png" alt="" className="h-10 w-auto" /><span className="font-display font-black text-5xl text-[#dc2626]">{String(i + 1).padStart(2, "0")}</span></div>
        <div><span className="block w-10 h-1 bg-[#dc2626] mb-2" /><p className="font-display font-black text-lg leading-tight">{l.title}</p></div>
      </div>
    )}
  </div>
);

export function LessonList() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const [prog, setProg] = useState({});
  useEffect(() => {
    if (user) { api.get("/me/lesson-progress").then((r) => setProg(Object.fromEntries(r.data.map((p) => [p.lesson_id, p])))).catch(() => {}); }
  }, [user]);
  useSeo("Ders Notları ve Eğitim Merkezi", "Özel güvenlik ders notları: mevzuat, güvenlik tedbirleri, ilk yardım, yangın güvenliği ve daha fazlası.");
  useEffect(() => {
    const t = setTimeout(() => api.get("/lessons", { params: { q } }).then((r) => setItems(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <>
      <section className="bg-zinc-950 text-white grid-lines">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid gap-6 md:grid-cols-[auto_1fr] items-center">
          <img src="/ada-logo.png" alt="Ada Özel Güvenlik logosu" className="h-24 sm:h-28 w-auto" />
          <div>
            <span className="eyebrow">Ada Özel Güvenlik Eğitim Merkezi</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight" data-testid="lessons-title">Ders Notları ve Eğitim Merkezi</h1>
            <p className="mt-3 text-zinc-300 text-sm sm:text-base">Derslerini öğren, önemli bilgileri tekrar et, sınava hazırlan</p>
            <div className="relative mt-6 max-w-xl"><Search size={18} className="absolute left-3 top-3.5 text-zinc-400" />
              <input className="field !pl-10 text-zinc-900" placeholder="Ders veya konu ara" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Ders ara" data-testid="lessons-search" /></div>
          </div>
        </div>
      </section>
      <Section>
        {!items ? <Loading /> : items.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="lessons-grid">
            {items.map((l, i) => (
              <Link key={l.id} to={`/ders-notlari/${l.slug}`} className="card-ada flex flex-col" data-testid={`lesson-card-${l.slug}`}>
                <LessonCover l={l} i={i} />
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex gap-2 mb-2">{l.locked ? <Badge tone="dark"><Lock size={10} className="mr-1" />Kursiyerlere özel</Badge> : <Badge>{l.topic_count} konu</Badge>}</div>
                  <h2 className="font-black text-lg leading-snug">{l.title}</h2>
                  <p className="text-sm text-zinc-600 mt-2 line-clamp-2 flex-1">{l.description}</p>
                  {prog[l.id] && <div className="mt-3" data-testid={`lesson-card-progress-${l.slug}`}><span className="flex justify-between text-xs font-bold"><span>İlerleme</span><span>%{prog[l.id].percent}</span></span><Progress value={prog[l.id].percent} className="h-1.5 mt-1" /></div>}
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#dc2626]">Dersi İncele <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        ) : <Empty text="Aramanıza uygun ders bulunamadı." />}
      </Section>
    </>
  );
}

function Pdfs({ pdfs }) {
  const [view, setView] = useState(null);
  if (!pdfs?.length) return null;
  return (
    <div className="card-ada p-5" data-testid="lesson-pdfs">
      <h2 className="font-black text-lg mb-3 flex items-center gap-2"><FileText size={18} className="text-[#dc2626]" />PDF ders notları ve kitapçıklar</h2>
      <div className="grid gap-2">
        {pdfs.map((p) => (
          <div key={p.id} className="border border-zinc-200 p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`pdf-${p.id}`}>
            <div><p className="font-bold text-sm">{p.title || "PDF dosyası"}</p>{p.description && <p className="text-xs text-zinc-500">{p.description}</p>}</div>
            <div className="flex gap-2">
              <button className="btn-dark !py-2 !px-3 text-xs" onClick={() => setView(p)} data-testid={`pdf-view-${p.id}`}><Eye size={14} />Görüntüle</button>
              {p.downloadable && <a className="btn-red !py-2 !px-3 text-xs" href={`${API_ORIGIN}/api/files/${p.file_id}?download=1`} data-testid={`pdf-download-${p.id}`}><Download size={14} />İndir</a>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-5xl w-[96vw] h-[90vh] rounded-none flex flex-col p-3" data-testid="pdf-dialog">
          <DialogHeader><DialogTitle className="pr-8">{view?.title}</DialogTitle></DialogHeader>
          {view && <iframe title={view.title} src={`${API_ORIGIN}/api/files/${view.file_id}#toolbar=${view.downloadable ? 1 : 0}`} className="flex-1 w-full border border-zinc-200" />}
          <p className="text-xs text-zinc-500 sm:hidden">PDF görünmüyorsa <a className="underline" href={`${API_ORIGIN}/api/files/${view?.file_id}`} target="_blank" rel="noreferrer">yeni sekmede açın</a>.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProgressBox({ l, p, user }) {
  const done = (p?.completed_topic_ids || []).filter((id) => l.topics.some((t) => t.id === id)).length;
  const total = l.topics.length;
  const last = l.topics.find((t) => t.id === p?.last_topic_id);
  const next = l.topics.find((t) => !(p?.completed_topic_ids || []).includes(t.id));
  const go = last && !(p.completed_topic_ids || []).includes(last.id) ? last : next;
  if (!total) return null;
  if (!user) return <p className="mt-5 text-sm text-zinc-300" data-testid="progress-login-hint"><Link to="/kursiyer-girisi" className="underline font-bold text-white">Kursiyer girişi</Link> yaparak okuduğun konuları işaretleyebilir ve kaldığın yerden devam edebilirsin.</p>;
  return (
    <div className="mt-6 max-w-xl" data-testid="lesson-progress">
      <span className="flex justify-between text-sm font-bold"><span>İlerlemen</span><span data-testid="lesson-progress-text">{done}/{total} konu • %{Math.round((done * 100) / total)}</span></span>
      <Progress value={(done * 100) / total} className="h-2 mt-2 bg-zinc-700" />
      {go ? <a href={`#konu-${go.id}`} className="btn-red mt-4 inline-flex max-w-full !whitespace-normal text-left" data-testid="lesson-continue-btn"><PlayCircle size={16} className="shrink-0" /><span className="min-w-0">{done || last ? "Kaldığın Yerden Devam Et" : "Derse Başla"}: {go.title}</span></a>
        : (p?.quiz_best || 0) >= l.pass_percent ? <a href={`${API_ORIGIN}/api/lessons/${l.slug}/certificate`} className="btn-red mt-4 inline-flex max-w-full !whitespace-normal" data-testid="certificate-download-btn"><Award size={16} /><span>Ders Tamamlama Belgesini İndir (PDF)</span></a>
        : <p className="mt-3 text-sm text-green-400 font-bold flex items-center gap-1" data-testid="lesson-all-done"><CheckCircle2 size={16} />Tüm konuları tamamladın. Ders sonu testini en az %{l.pass_percent} ile geçip tamamlama belgeni al.</p>}
    </div>
  );
}

export function LessonDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setL(null); api.get(`/lessons/${slug}`).then((r) => { setL(r.data); setP(r.data.progress); }).catch((e) => setErr(errMsg(e))); }, [slug, user]);
  useSeo(l?.title ? `${l.title} – Ders Notları` : "Ders Notları", l?.description);
  const mark = async (topic_id, done) => {
    try { setP((await api.post(`/lessons/${slug}/progress`, { topic_id, done })).data); } catch (x) { toast.error(errMsg(x)); }
  };
  const isDone = (id) => (p?.completed_topic_ids || []).includes(id);
  if (err) return <Section><Empty text={err} testid="lesson-error" /><div className="mt-4 flex gap-2"><Link to="/ders-notlari" className="btn-dark">Ders Notları</Link><Link to="/kursiyer-girisi" className="btn-red">Kursiyer Girişi</Link></div></Section>;
  if (!l) return <Loading />;
  return (
    <>
      <section className="bg-zinc-950 text-white grid-lines">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/ders-notlari" className="text-xs text-zinc-400 hover:text-white">← Ders Notları</Link>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight" data-testid="lesson-title">{l.title}</h1>
          <p className="mt-3 text-zinc-300 max-w-3xl text-sm sm:text-base">{l.description}</p>
          {!l.published && <Badge tone="amber">Taslak – yalnızca yöneticiler görüyor</Badge>}
          <ProgressBox l={l} p={p} user={user} />
        </div>
      </section>
      <Section className="!pt-8">
        <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
          <aside className="lg:sticky lg:top-24 h-fit min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Konu başlıkları</p>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2" data-testid="lesson-toc">
              {l.topics.map((t, i) => <a key={t.id} href={`#konu-${t.id}`} onClick={() => user && mark(t.id, null)} className={`shrink-0 text-sm px-3 py-2 border-l-4 hover:border-[#dc2626] hover:bg-zinc-50 flex gap-1.5 items-start ${p?.last_topic_id === t.id ? "border-[#dc2626] bg-zinc-50" : "border-zinc-200"}`} data-testid={`toc-${t.id}`}>{isDone(t.id) ? <CheckCircle2 size={15} className="text-green-600 shrink-0 mt-0.5" data-testid={`toc-done-${t.id}`} /> : <b className="text-[#dc2626]">{i + 1}.</b>}{t.title}</a>)}
              {l.quiz_available && <a href="#ders-testi" className="shrink-0 text-sm px-3 py-2 border-l-4 border-zinc-950 font-bold flex gap-1.5 items-center" data-testid="toc-quiz"><ClipboardCheck size={15} className="text-[#dc2626]" />Ders Sonu Testi</a>}
            </nav>
          </aside>
          <div className="grid gap-8 min-w-0">
            {l.topics.length ? l.topics.map((t, i) => (
              <article key={t.id} id={`konu-${t.id}`} className="scroll-mt-24 border-b border-zinc-200 pb-8" data-testid={`topic-${t.id}`}>
                <p className="text-xs font-bold text-[#dc2626] uppercase">Konu {i + 1}</p>
                <h2 className="text-2xl font-black mt-1">{t.title}</h2>
                {t.subtitle && <p className="text-zinc-600 mt-1">{t.subtitle}</p>}
                <div className="lesson-content mt-5" dangerouslySetInnerHTML={{ __html: rewrite(t.content_html) }} />
                {embedUrl(t.video_url) && <div className="aspect-video mt-5 bg-black"><iframe src={embedUrl(t.video_url)} title={t.title} className="w-full h-full" allowFullScreen allow="encrypted-media; picture-in-picture; fullscreen" /></div>}
                {user && <button className={`mt-5 ${isDone(t.id) ? "btn-dark" : "btn-red"} !py-2 text-sm`} onClick={() => mark(t.id, !isDone(t.id))} data-testid={`topic-done-btn-${t.id}`}>
                  {isDone(t.id) ? <><CheckCircle2 size={16} className="text-green-400" />Okundu – işareti kaldır</> : <><Circle size={16} />Okudum olarak işaretle</>}</button>}
              </article>
            )) : <Empty text="Bu dersin notları hazırlanıyor." testid="lesson-empty" />}
            <Pdfs pdfs={l.pdfs} />
            {l.quiz_available && <LessonQuiz slug={slug} user={user} best={p?.quiz_best} onDone={(pct) => user && setP({ ...(p || {}), quiz_last: pct, quiz_best: Math.max(pct, p?.quiz_best || 0) })} />}
            <div className="flex flex-wrap gap-2"><Link to="/deneme-sinavlari" className="btn-red"><BookOpen size={16} />Deneme Sınavı Çöz</Link><Link to="/ders-notlari" className="btn-dark">Tüm Dersler</Link></div>
          </div>
        </div>
      </Section>
    </>
  );
}
